import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  type, 
  message, 
  duration = 5000, 
  onClose 
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-white" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-white" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-white" />;
      case 'info':
        return <Info className="w-5 h-5 text-white" />;
      default:
        return null;
    }
  };

  const getToastClasses = () => {
    // Réduction de la taille minimale et adaptation pour mobile
    const baseClasses = "rounded-lg shadow-lg p-3 flex items-start gap-2 w-full sm:min-w-[280px] max-w-[90vw] sm:max-w-sm text-white";
    
    switch (type) {
      case 'success':
        return `${baseClasses} bg-green-600 dark:bg-green-700`;
      case 'error':
        return `${baseClasses} bg-red-600 dark:bg-red-700`;
      case 'warning':
        return `${baseClasses} bg-amber-600 dark:bg-amber-700`;
      case 'info':
        return `${baseClasses} bg-blue-600 dark:bg-blue-700`;
      default:
        return baseClasses;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
      className={getToastClasses()}
    >
      <div className="flex-shrink-0">
        {getIcon()}
      </div>
      <div className="flex-1">
        <p className="text-xs sm:text-sm font-medium text-white break-words">{message}</p>
      </div>
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 text-white/80 hover:text-white focus:outline-none"
      >
        <X className="w-3 h-3 sm:w-4 sm:h-4" />
      </button>
    </motion.div>
  );
};

export default Toast;
